import { Table } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {
  fetchSupportTickets,
  updateSupportTicketStatus,
  createSupportTicket,
} from "../../../../services/supportService";
import { encrypt } from "../../../../utils/crypto";
import TooltipWrapper from "../../../../utils/TooltipWrapper";
import SupportTicketStatusBadge from "./components/SupportTicketStatusBadge";
import CreateTicketModal from "./components/CreateTicketModal";
import timeAgo from "../../../../utils/timeAgo";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { companyID } from "../../../../auth";

dayjs.extend(utc);

const ticketStatusOptions = [
  { label: "Open", value: "OPEN" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Resolved", value: "RESOLVED" },
  { label: "Closed", value: "CLOSED" },
  { label: "Pending Info", value: "PENDING_INFO" },
];

const priorityOptions = [
  { label: "Low", value: "LOW" },
  { label: "Medium", value: "MEDIUM" },
  { label: "High", value: "HIGH" },
  { label: "Critical", value: "CRITICAL" },
];

const SupportTicketList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 10,
    search: null,
    status: null,
    priority: null,
    category: null,
    assigned_agent: null,
    date_from: null,
    date_to: null,
    sort_by: "created_at",
    order: "DESC",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const categoryOptions = [
    { label: "Payroll", value: "PAYROLL" },
    { label: "Attendance", value: "ATTENDANCE" },
    { label: "Bug Report", value: "BUG" },
    { label: "Leave", value: "LEAVE" },
    { label: "Feature Request", value: "FEATURE_REQUEST" },
    { label: "Other", value: "OTHER" },
  ];

  const fetchTicketsList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        per_page: filters.pageSize,
      };

      // Add all filter parameters except pagination
      Object.keys(filters).forEach((key) => {
        if (
          filters[key] !== null &&
          filters[key] !== undefined &&
          key !== "page" &&
          key !== "pageSize"
        ) {
          params[key] = filters[key];
        }
      });
 
      const response = await fetchSupportTickets(params);
 

      // Handle new API response structure
      if (response?.content) {
        setTickets(response.content || []);

        // Set pagination from API response
        setPagination({
          current_page: response.pageNumber || filters.page,
          per_page: response.pageSize || filters.pageSize,
          total: response.totalElements || 0,
          total_pages: response.totalPages || 1,
        });
      } else {
        console.error("API response missing content:", response);
        setTickets([]);
        setPagination({
          current_page: 1,
          per_page: filters.pageSize,
          total: 0,
          total_pages: 1,
        });
      }
    } catch (error) {
      console.error("Error fetching support tickets:", error);
      toast.error(t("Failed to fetch support tickets"));
      setTickets([]);
      setPagination({
        current_page: 1,
        per_page: filters.pageSize,
        total: 0,
        total_pages: 1,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchTicketsList();
  }, [fetchTicketsList]);

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page when filters change
    }));
  };

  const updatePagination = (page, pageSize) => {
    setFilters((prev) => ({
      ...prev,
      page: parseInt(page) || 1,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handleView = (record) => {
    if (!record?.id) return;
    const encryptedId = encrypt(record.id.toString());
    navigate(`${encryptedId}`);
  };

  const handleEdit = (record) => {
    if (!record?.id) return;
    setSelectedTicket(record);
    setModalOpen(true);
  };

  const handleNewTicket = () => {
    setSelectedTicket(null); // null indicates new ticket mode
    setModalOpen(true);
  };

  const handleModalSave = async (formData) => {
    setModalSubmitting(true);
    try {
      let response;
      if (selectedTicket) {
        // Edit existing ticket
        response = await updateSupportTicketStatus(
          selectedTicket.id,
          formData.status,
        );
        toast.success(response?.message || t("Ticket updated successfully"));
      } else {
        // Create new ticket
        response = await createSupportTicket(formData);
        toast.success(response?.message || t("Ticket created successfully"));
      }

      setModalOpen(false);
      setSelectedTicket(null);
      fetchTicketsList(); // Refresh the list
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        (selectedTicket
          ? t("Unable to update ticket")
          : t("Unable to create ticket"));
      toast.error(message);
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTicket(null);
  };

  const columns = [
    {
      title: t("Ticket ID"),
      dataIndex: "ticketCode",
      key: "ticketCode",
      width: 180,
      render: (text) => <span className="text-primary fw-medium">{text}</span>,
    },
    {
      title: t("Subject"),
      dataIndex: "subject",
      key: "subject",
      render: (text, record) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.description && (
            <small className="text-muted">
              {record.description.length > 50
                ? record.description.substring(0, 50) + "..."
                : record.description}
            </small>
          )}
        </div>
      ),
    },
    {
      title: t("Category"),
      dataIndex: "category",
      key: "category",
      width: 130,
      render: (category) => {
        const categoryLabel =
          categoryOptions.find((opt) => opt.value === category)?.label ||
          category;
        return (
          <span className="badge bg-info text-white">{categoryLabel}</span>
        );
      },
    },
    {
      title: t("Priority"),
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => <SupportTicketStatusBadge status={priority} type="priority" />,
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => <SupportTicketStatusBadge status={status} />,
    },
    {
      title: t("Created On"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (timestamp) => timeAgo(dayjs.utc(timestamp).local().format()),
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="btn-group" role="group">
          <TooltipWrapper tooltipText={t("View Details")}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleView(record)}
              type="button"
            >
              <i className="fa fa-eye" />
            </button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  const companyId = companyID();

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Support Tickets")}
          title={t("Dashboard")}
          subtitle={t("Support Tickets")}
          name={t("Support Tickets")}
        />

        <div className="row">
          <div className="col-sm-12">
            <Card>
              <Card.Header className="px-3 pt-3 py-0">
                <div
                  className="d-flex flex-md-nowrap flex-wrap justify-content-between align-items-center gap-1"
                  style={{ minHeight: "40px" }}
                >
                  <div
                    className="d-flex align-items-center gap-2 flex-grow-1"
                    style={{ minWidth: 0, overflow: "hidden" }}
                  >
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        updateFilters({
                          search: searchTerm ? searchTerm.trim() : null,
                        });
                      }}
                      className="flex-shrink-1"
                    >
                      <InputGroup
                        className=""
                        style={{
                          width: "clamp(200px, 50vw, 20rem)",
                          maxWidth: "100%",
                        }}
                      >
                        <Form.Control
                          className="form-control"
                          placeholder={t("Search by ID, subject, email")}
                          style={{
                            height: "2.4rem",
                          }}
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            if (e.target.value.trim() === "") {
                              updateFilters({ search: null });
                            }
                          }}
                        />
                        <Button type="submit" variant="primary">
                          <i className="fa fa-search" />
                        </Button>
                      </InputGroup>
                    </Form>
                  </div>
                  {companyId !== 1 && (
                    <Button
                      variant="success"
                      className="flex-shrink-0 mt-md-0"
                      onClick={handleNewTicket}
                    >
                      <i className="fa fa-plus me-2" />
                      <span className="d-none d-sm-inline">
                        {t("Create New Ticket")}
                      </span>
                      <span className="d-inline d-sm-none">{t("New")}</span>
                    </Button>
                  )}

                  {/* Advanced Filters Toggle */}
                  <Button
                    variant="outline-secondary"
                    className="flex-shrink-0"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  >
                    <i className="fa fa-filter me-2" />
                    <span className="d-none d-sm-inline">{t("Filters")}</span>
                    <i
                      className={`fa fa-chevron-${showAdvancedFilters ? "up" : "down"} ms-2`}
                    />
                  </Button>
                </div>

                {/* Active Filter Tags */}
                {(filters.status ||
                  filters.priority ||
                  filters.category ||
                  filters.date_from ||
                  filters.date_to) && (
                  <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                    <span className="small text-muted fw-semibold">
                      {t("Active Filters")}:
                    </span>

                    {filters.status && (
                      <span className="badge bg-primary d-flex align-items-center gap-1">
                        Status:{" "}
                        {
                          ticketStatusOptions.find(
                            (s) => s.value === filters.status,
                          )?.label
                        }
                        <button
                          className="btn-close btn-close-white"
                          style={{ fontSize: "10px" }}
                          onClick={() => updateFilters({ status: null })}
                        ></button>
                      </span>
                    )}

                    {filters.priority && (
                      <span className="badge bg-info d-flex align-items-center gap-1">
                        Priority:{" "}
                        {
                          priorityOptions.find(
                            (p) => p.value === filters.priority,
                          )?.label
                        }
                        <button
                          className="btn-close btn-close-white"
                          style={{ fontSize: "10px" }}
                          onClick={() => updateFilters({ priority: null })}
                        ></button>
                      </span>
                    )}

                    {filters.category && (
                      <span className="badge bg-secondary d-flex align-items-center gap-1">
                        Category:{" "}
                        {
                          categoryOptions.find(
                            (c) => c.value === filters.category,
                          )?.label
                        }
                        <button
                          className="btn-close btn-close-white"
                          style={{ fontSize: "10px" }}
                          onClick={() => updateFilters({ category: null })}
                        ></button>
                      </span>
                    )}

                    {(filters.date_from || filters.date_to) && (
                      <span className="badge bg-dark d-flex align-items-center gap-1">
                        Date: {filters.date_from || "..."} to{" "}
                        {filters.date_to || "..."}
                        <button
                          className="btn-close btn-close-white"
                          style={{ fontSize: "10px" }}
                          onClick={() =>
                            updateFilters({ date_from: null, date_to: null })
                          }
                        ></button>
                      </span>
                    )}
                  </div>
                )}

                {/* Advanced Filters Panel */}
                {showAdvancedFilters && (
                  <div className="border-top pt-3 mt-3">
                    <div className="row g-3">
                      {/* Status Filter */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Status")}
                          </Form.Label>
                          <Form.Select
                            value={filters.status || ""}
                            onChange={(e) =>
                              updateFilters({ status: e.target.value || null })
                            }
                            size="sm"
                          >
                            <option value="">{t("All Status")}</option>
                            {ticketStatusOptions.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Priority Filter */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Priority")}
                          </Form.Label>
                          <Form.Select
                            value={filters.priority || ""}
                            onChange={(e) =>
                              updateFilters({
                                priority: e.target.value || null,
                              })
                            }
                            size="sm"
                          >
                            <option value="">{t("All Priorities")}</option>
                            {priorityOptions.map((priority) => (
                              <option
                                key={priority.value}
                                value={priority.value}
                              >
                                {priority.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Category Filter */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Category")}
                          </Form.Label>
                          <Form.Select
                            value={filters.category || ""}
                            onChange={(e) =>
                              updateFilters({
                                category: e.target.value || null,
                              })
                            }
                            size="sm"
                          >
                            <option value="">{t("All Categories")}</option>
                            {categoryOptions.map((category) => (
                              <option
                                key={category.value}
                                value={category.value}
                              >
                                {category.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Date From */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Date From")}
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={filters.date_from || ""}
                            onChange={(e) =>
                              updateFilters({
                                date_from: e.target.value || null,
                              })
                            }
                            size="sm"
                          />
                        </Form.Group>
                      </div>

                      {/* Date To */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Date To")}
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={filters.date_to || ""}
                            onChange={(e) =>
                              updateFilters({ date_to: e.target.value || null })
                            }
                            size="sm"
                          />
                        </Form.Group>
                      </div>

                      {/* Sort By */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Sort By")}
                          </Form.Label>
                          <Form.Select
                            value={filters.sort_by || "created_at"}
                            onChange={(e) =>
                              updateFilters({ sort_by: e.target.value })
                            }
                            size="sm"
                          >
                            <option value="created_at">
                              {t("Created Date")}
                            </option>
                            <option value="id">{t("Ticket ID")}</option>
                            <option value="subject">{t("Subject")}</option>
                            <option value="status">{t("Status")}</option>
                            <option value="priority">{t("Priority")}</option>
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Order */}
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label className="small fw-semibold">
                            {t("Order")}
                          </Form.Label>
                          <Form.Select
                            value={filters.order || "DESC"}
                            onChange={(e) =>
                              updateFilters({ order: e.target.value })
                            }
                            size="sm"
                          >
                            <option value="DESC">{t("Descending")}</option>
                            <option value="ASC">{t("Ascending")}</option>
                          </Form.Select>
                        </Form.Group>
                      </div>

                      {/* Reset Filters Button */}
                      <div className="col-md-12 d-flex justify-content-end">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setFilters({
                              page: 1,
                              pageSize: 10,
                              search: null,
                              status: null,
                              priority: null,
                              category: null,
                              assigned_agent: null,
                              date_from: null,
                              date_to: null,
                              sort_by: "created_at",
                              order: "DESC",
                            });
                            setSearchTerm("");
                          }}
                        >
                          <i className="fa fa-refresh me-2" />
                          {t("Reset All Filters")}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Card.Header>

              <Card.Body className="p-2 p-md-3">
                <div className="table-responsive">
                  <Table
                    className="table-striped"
                    columns={columns}
                    dataSource={tickets}
                    loading={loading}
                    rowKey={(record) => record.id || record.ticketCode}
                    pagination={false}
                  />
                </div>

                {/* Custom Pagination Outside Table - Left and Right Sides */}
                <div
                  className="d-flex justify-content-between align-items-center mt-3 px-3 py-2"
                  style={{
                    borderTop: "1px solid #f0f0f0",
                    backgroundColor: "#fafafa",
                    borderRadius: "0 0 8px 8px",
                    minHeight: "50px",
                  }}
                >
                  {/* LEFT SIDE - Total count */}
                  <div className="d-flex align-items-center">
                    <div
                      className="pagination-info-box"
                      style={{
                        background: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        fontSize: "14px",
                        color: "#666",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        fontWeight: "500",
                      }}
                    >
                      {pagination.total > 0
                        ? `${
                            (pagination.current_page - 1) *
                              pagination.per_page +
                            1
                          }-${Math.min(
                            pagination.current_page * pagination.per_page,
                            pagination.total,
                          )} of ${pagination.total} ${t("tickets")}`
                        : `0 ${t("tickets")}`}
                    </div>
                  </div>

                  {/* RIGHT SIDE - Pagination controls */}
                  <div className="d-flex align-items-center gap-3">
                    {/* Page size selector */}
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {filters.pageSize} / page
                      </span>

                      <select
                        className="form-select form-select-sm"
                        value={filters.pageSize}
                        onChange={(e) =>
                          updatePagination(1, parseInt(e.target.value))
                        }
                        style={{
                          border: "1px solid #d9d9d9",
                          borderRadius: "4px",
                          padding: "6px 10px",
                          fontSize: "14px",
                          width: "110px",
                          height: "32px",
                        }}
                      >
                        <option value={5}>5 / page</option>
                        <option value={10}>10 / page</option>
                        <option value={20}>20 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>

                    {/* Page navigation */}
                    <div className="d-flex align-items-center gap-1">
                      <button
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                        disabled={filters.page <= 1}
                        onClick={() =>
                          updatePagination(
                            filters.page - 1,
                            filters.pageSize,
                          )
                        }
                        style={{
                          width: "32px",
                          height: "32px",
                          padding: "0",
                          border: "1px solid #d9d9d9",
                          borderRadius: "4px",
                        }}
                      >
                        <i
                          className="fa fa-chevron-left"
                          style={{ fontSize: "12px" }}
                        ></i>
                      </button>

                      {Array.from(
                        { length: Math.min(5, pagination.total_pages) },
                        (_, i) => {
                          const startPage = Math.max(
                            1,
                            filters.page - 2,
                          );
                          const pageNum = startPage + i;

                          if (pageNum > pagination.total_pages) return null;

                          return (
                            <button
                              key={pageNum}
                              className={`btn btn-sm d-flex align-items-center justify-content-center ${
                                pageNum === filters.page
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() =>
                                updatePagination(pageNum, filters.pageSize)
                              }
                              style={{
                                width: "32px",
                                height: "32px",
                                padding: "0",
                                fontSize: "14px",
                                fontWeight:
                                  pageNum === filters.page
                                    ? "600"
                                    : "400",
                                border: "1px solid #d9d9d9",
                                borderRadius: "4px",
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}

                      <button
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                        disabled={
                          filters.page >= pagination.total_pages
                        }
                        onClick={() =>
                          updatePagination(
                            filters.page + 1,
                            filters.pageSize,
                          )
                        }
                        style={{
                          width: "32px",
                          height: "32px",
                          padding: "0",
                          border: "1px solid #d9d9d9",
                          borderRadius: "4px",
                        }}
                      >
                        <i
                          className="fa fa-chevron-right"
                          style={{ fontSize: "12px" }}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>

        <CreateTicketModal
          show={modalOpen}
          onClose={handleModalClose}
          ticket={selectedTicket}
          onSave={handleModalSave}
          submitting={modalSubmitting}
        />
      </div>
    </div>
  );
};

export default SupportTicketList;
